import React, { useCallback, useContext, useMemo, useState } from 'react';
import classes from './SourceLevelAction.module.scss';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { FindContentContext } from '../FindContentProvider';
import { UseFormReturn } from 'react-hook-form';
import { ImportSourceArgsGroupInput } from '../../../../models/graphql';
import { AddEditSourceModal } from '../AddEditSourceModal';
import { CreateEditImporterModal } from '../../AutoImporters/CreateEditImporterModal';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { MaxSelectedPostsToImport } from '../../../../constants/find-content';
import { showToast } from '../../../../helpers/showToast';
import pluralize from 'pluralize';
import { AppContext } from 'providers/AppProvider';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type SourceLevelActionViewProps = {
  formInstance: UseFormReturn;
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getArgsHandler: () => ImportSourceArgsGroupInput;
};

export const SourceLevelActionView = (props: SourceLevelActionViewProps): JSX.Element => {
  const { formInstance, showSaveSourceModal, setShowSaveSourceModal, getArgsHandler } = props;
  const { posts, loading, error, sourceType, selectedPosts, setSelectedPosts } = useContext(FindContentContext);
  const { setLimitReachedModalState } = useContext(AppContext);
  const { canAddNewAutoImport, canSaveSource } = useOrganizationPlan();
  const [showCreateAutoImport, setShowCreateAutoImport] = useState<boolean>(false);

  const disableAutoImport = useMemo(() => {
    let disabled;

    // We allow auto import if posts set is empty and no errors
    if (posts.length === 0 && !error) {
      disabled = false;
    } else {
      // We allow auto import only if posts have date
      disabled = posts.some(post => !Boolean(post.createdAt));
    }

    return !!error || disabled;
  }, [error, posts]);

  const handleSelectAllCheckedState = useCallback(
    (isChecked: boolean) => {
      const selectAll: Record<string, string>[] = [];

      if (isChecked) {
        if (posts.length > MaxSelectedPostsToImport) {
          const slicedArray = posts.slice(0, MaxSelectedPostsToImport);
          slicedArray.forEach(p => {
            selectAll.push({ id: p.externalId });
          });
          showToast({
            severity: 'warn',
            detail: `Note: only ${pluralize(
              'post',
              MaxSelectedPostsToImport,
              true
            )} were selected as it's the maximum number that can be bulk imported in one go.`,
            summary: 'Selection Limit'
          });
        } else {
          posts.forEach(p => {
            selectAll.push({ id: p.externalId });
          });
        }
      }

      setSelectedPosts(selectAll);
    },
    [posts, setSelectedPosts]
  );

  const selectAllStateCheckbox = useMemo((): JSX.Element => {
    let selectAllState;
    if (selectedPosts.length === 0) {
      selectAllState = { checked: false };
    } else if (posts.length > selectedPosts.length) {
      selectAllState = { indeterminate: true };
    } else if (posts.length === selectedPosts.length) {
      selectAllState = { checked: true };
    }

    return (
      <Checkbox
        {...{
          ...selectAllState,
          formInstance: formInstance,
          name: 'PostSelectAllCheckBox',
          rules: {},
          size: 'xlarge',
          label: 'Select All',
          action: handleSelectAllCheckedState
        }}
      />
    );
  }, [formInstance, handleSelectAllCheckedState, posts, selectedPosts]);

  return (
    <>
      {!loading && (
        <div className={classes.SelectLevelActionWrapper}>
          <div className={'grid-x grid-padding-x align-justify'}>
            <div className={classes.CheckboxWrapper}>{!error && posts.length > 0 && selectAllStateCheckbox}</div>
            <div className={classes.ButtonBarWrapper}>
              <div id={'disabledSaveSourceButtonTooltip'} className={classes.SaveSourceButtonWrapper}>
                {error && (
                  <Tooltip type={'regular'} target={'#disabledSaveSourceButtonTooltip'} position={'bottom'}>
                    <div>You cannot save this feed as a source, as we did not find valid data.</div>
                  </Tooltip>
                )}
                <Button
                  color={ButtonColorsEnum.primary}
                  size={'medium'}
                  type={'button'}
                  variant={'hollow'}
                  disabled={!!error}
                  action={() =>
                    canSaveSource()
                      ? setShowSaveSourceModal(true)
                      : setLimitReachedModalState({
                          isOpen: true,
                          type: 'importSource'
                        })
                  }>
                  Save Source
                </Button>
              </div>
              {!sourceType && (
                <div id={'disableAutoImportButtonTooltip'} className={classes.AutoImportButtonWrapper}>
                  {disableAutoImport && (
                    <Tooltip type={'regular'} target={'#disableAutoImportButtonTooltip'} position={'bottom'}>
                      <>
                        {error ? (
                          <div>You cannot save this feed as an Auto-Import source, as we did not find valid data.</div>
                        ) : (
                          <div>
                            The RSS feed lacks timestamps, which are needed to track new posts. As a result, you can’t
                            save this feed as an Auto-Import source.
                          </div>
                        )}
                      </>
                    </Tooltip>
                  )}
                  <Button
                    color={ButtonColorsEnum.primary}
                    size={'medium'}
                    type={'button'}
                    variant={'hollow'}
                    disabled={disableAutoImport}
                    action={() => {
                      if (canAddNewAutoImport()) {
                        setShowCreateAutoImport(true);
                      } else {
                        setLimitReachedModalState({
                          isOpen: true,
                          type: 'autoRSS'
                        });
                      }
                    }}>
                    Set Auto-Import
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showSaveSourceModal && (
        <AddEditSourceModal args={getArgsHandler()} closeModalHandler={() => setShowSaveSourceModal(false)} />
      )}
      {showCreateAutoImport && (
        <CreateEditImporterModal
          {...{
            onClose: () => setShowCreateAutoImport(false),
            newRssAutoImport: {
              url: formInstance.getValues('url'),
              term: formInstance.getValues('term')
            }
          }}
        />
      )}
    </>
  );
};
